// Diff two workflow editor states into small ops, and apply ops to a state.
// Used by the Remote Support collab layer: every local edit is diffed
// (prev vs next) and only the changed nodes/connections travel over the
// wire — never the whole state (except the one-time join bootstrap).
//
// Conflict rule: last-writer-wins per node / per connection. Ops are pure —
// applyOps returns a new state object and never mutates the input.
//
// Op shapes:
//   {op:'node_add',    node}
//   {op:'node_update', node}            (whole node object, LWW)
//   {op:'node_remove', nodeId}
//   {op:'conn_add',    connection}
//   {op:'conn_remove', key}             (key = stable stringify of the connection)
//   {op:'groups_set',  groups}
//   {op:'meta_update', fields}          ({workflowName?, aiInstruction?, checkSilentError?})

function connectionKey(connection) {
  return JSON.stringify(connection);
}

function nodesById(nodes) {
  var map = {};
  (nodes || []).forEach(function(node) {
    if (node && node.id != null) map[node.id] = node;
  });
  return map;
}

function sameNode(a, b) {
  if (a === b) return true;
  try { return JSON.stringify(a) === JSON.stringify(b); } catch (_) { return false; }
}

function diffWorkflowStates(prev, next) {
  var ops = [];
  if (!prev || !next || prev === next) return ops;

  // Nodes — reference check first so untouched nodes cost nothing.
  if (prev.nodes !== next.nodes) {
    var prevMap = nodesById(prev.nodes);
    var nextMap = nodesById(next.nodes);
    (next.nodes || []).forEach(function(node) {
      if (!node || node.id == null) return;
      var before = prevMap[node.id];
      if (!before) {
        ops.push({ op: 'node_add', node: node });
      } else if (!sameNode(before, node)) {
        ops.push({ op: 'node_update', node: node });
      }
    });
    (prev.nodes || []).forEach(function(node) {
      if (!node || node.id == null) return;
      if (!nextMap[node.id]) ops.push({ op: 'node_remove', nodeId: node.id });
    });
  }

  // Connections — identified by their full stringified shape.
  if (prev.connections !== next.connections) {
    var prevKeys = {};
    (prev.connections || []).forEach(function(connection) { prevKeys[connectionKey(connection)] = true; });
    var nextKeys = {};
    (next.connections || []).forEach(function(connection) {
      var key = connectionKey(connection);
      nextKeys[key] = true;
      if (!prevKeys[key]) ops.push({ op: 'conn_add', connection: connection });
    });
    Object.keys(prevKeys).forEach(function(key) {
      if (!nextKeys[key]) ops.push({ op: 'conn_remove', key: key });
    });
  }

  // Groups — small array, replace wholesale when it changed.
  if (prev.groups !== next.groups && JSON.stringify(prev.groups || []) !== JSON.stringify(next.groups || [])) {
    ops.push({ op: 'groups_set', groups: next.groups || [] });
  }

  var meta = {};
  var hasMeta = false;
  ['workflowName', 'aiInstruction', 'checkSilentError'].forEach(function(field) {
    if (prev[field] !== next[field]) {
      meta[field] = next[field];
      hasMeta = true;
    }
  });
  if (hasMeta) ops.push({ op: 'meta_update', fields: meta });

  return ops;
}

function applyOps(state, ops) {
  if (!state || !Array.isArray(ops) || !ops.length) return state;
  var next = Object.assign({}, state);
  next.nodes = (state.nodes || []).slice();
  next.connections = (state.connections || []).slice();

  ops.forEach(function(entry) {
    if (!entry || !entry.op) return;
    if (entry.op === 'node_add' || entry.op === 'node_update') {
      var found = false;
      for (var i = 0; i < next.nodes.length; i++) {
        if (next.nodes[i] && next.nodes[i].id === entry.node.id) {
          next.nodes[i] = entry.node;
          found = true;
          break;
        }
      }
      if (!found) next.nodes.push(entry.node);
    } else if (entry.op === 'node_remove') {
      next.nodes = next.nodes.filter(function(node) { return !node || node.id !== entry.nodeId; });
    } else if (entry.op === 'conn_add') {
      var key = connectionKey(entry.connection);
      var exists = next.connections.some(function(connection) { return connectionKey(connection) === key; });
      if (!exists) next.connections.push(entry.connection);
    } else if (entry.op === 'conn_remove') {
      next.connections = next.connections.filter(function(connection) { return connectionKey(connection) !== entry.key; });
    } else if (entry.op === 'groups_set') {
      next.groups = entry.groups;
    } else if (entry.op === 'meta_update') {
      Object.assign(next, entry.fields);
    }
  });

  return next;
}

module.exports = { diffWorkflowStates, applyOps };
